import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoSend,
  IoCloseCircle,
  IoImageOutline,
  IoMicOutline,
  IoClose,
  IoTrashOutline,
  IoStopCircle,
  IoPauseCircle,
  IoPlayCircle,
} from 'react-icons/io5';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import VoicePlayer from './VoicePlayer';
import './MessageInput.css';

const MessageInput = ({ onSend, onTyping, onStopTyping, onRecording, onStopRecording, replyTo, onCancelReply, disabled }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceBlobUrl, setVoiceBlobUrl] = useState(null);
  const [sending, setSending] = useState(false);

  // Inline recorder state
  const [recording, setRecording] = useState(false);
  const [recPaused, setRecPaused] = useState(false);
  const [recDuration, setRecDuration] = useState(0);
  const recWaveRef = useRef(null);
  const recWsRef = useRef(null);
  const recPluginRef = useRef(null);
  const recTimerRef = useRef(null);
  const recStoppingRef = useRef(false);
  const recSendAfterStopRef = useRef(false); // Flag to send after stopping
  const recCancelRef = useRef(false); // Flag to track if we're cancelling (deleting) the recording
  const voiceBlobUrlRef = useRef(null); // Track blob URL for immediate access
  const recMaxDuration = 60;

  const typingTimeoutRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Focus input when replying
  useEffect(() => {
    if (replyTo && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyTo]);

  // Clean up previews on unmount
  useEffect(() => {
    return () => {
      previews.forEach(p => URL.revokeObjectURL(p.url));
      if (voiceBlobUrl) URL.revokeObjectURL(voiceBlobUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setContent(e.target.value);

    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY = textarea.scrollHeight > 120 ? 'auto' : 'hidden';

    if (!isTyping) {
      setIsTyping(true);
      onTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      onStopTyping();
    }, 2000);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;

    const maxFiles = 4 - files.length;
    const newFiles = selected.slice(0, maxFiles);

    const newPreviews = newFiles.map(file => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
      name: file.name,
    }));

    setFiles(prev => [...prev, ...newFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index].url);
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeVoice = () => {
    if (voiceBlobUrl) URL.revokeObjectURL(voiceBlobUrl);
    voiceBlobUrlRef.current = null;
    setVoiceBlob(null);
    setVoiceBlobUrl(null);
  };

  // --- Inline voice recorder ---
  const startRecording = useCallback(async () => {
    if (!recWaveRef.current) return;

    recWaveRef.current.innerHTML = '';
    setRecDuration(0);
    setRecPaused(false);
    recStoppingRef.current = false;

    const ws = WaveSurfer.create({
      container: recWaveRef.current,
      waveColor: '#1877f2',
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 28,
      normalize: true,
    });

    const rec = ws.registerPlugin(RecordPlugin.create({
      scrollingWaveform: false,
      continuousWaveform: true,
      continuousWaveformDuration: recMaxDuration,
      renderRecordedAudio: false,
      audioBitsPerSecond: 128000,
      mimeType: 'audio/webm;codecs=opus',
    }));

    recWsRef.current = ws;
    recPluginRef.current = rec;

    rec.on('record-end', async (blob) => {
      const shouldSend = recSendAfterStopRef.current;
      const isCancelled = recCancelRef.current;
      recSendAfterStopRef.current = false;
      recCancelRef.current = false;
      cleanupRecorder();

      // If cancelled, don't save the recording
      if (isCancelled) {
        setRecording(false);
        if (onStopRecording) onStopRecording();
        return;
      }

      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
        setVoiceBlob(blob);
        setVoiceBlobUrl(url);
        voiceBlobUrlRef.current = url;

        // If clicked Send while recording, send now
        if (shouldSend) {
          try {
            await onSend('', [], blob);
            // Reset state after send
            URL.revokeObjectURL(url);
            voiceBlobUrlRef.current = null;
            setVoiceBlob(null);
            setVoiceBlobUrl(null);
            setRecDuration(0);
            setSending(false);
          } catch (err) {
            console.error('Failed to send voice:', err);
            setSending(false);
          }
        }
      }
      setRecording(false);
      if (onStopRecording) onStopRecording();
      recStoppingRef.current = false;
    });

    try {
      await rec.startRecording();
      setRecording(true);
      if (onRecording) onRecording();

      let elapsed = 0;
      recTimerRef.current = setInterval(() => {
        elapsed += 1;
        setRecDuration(elapsed);
        if (elapsed >= recMaxDuration) {
          clearInterval(recTimerRef.current);
          recTimerRef.current = null;
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      console.error('Failed to start recording:', err);
      cleanupRecorder();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = useCallback(() => {
    if (recStoppingRef.current) return;
    recStoppingRef.current = true;

    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }

    try {
      if (recPluginRef.current?.isRecording()) {
        recPluginRef.current.stopRecording();
      } else {
        setRecording(false);
        recStoppingRef.current = false;
      }
    } catch {
      recStoppingRef.current = false;
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (recPluginRef.current?.pauseRecording) {
      recPluginRef.current.pauseRecording();
      setRecPaused(true);
      if (recTimerRef.current) {
        clearInterval(recTimerRef.current);
        recTimerRef.current = null;
      }
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (recPluginRef.current?.resumeRecording) {
      recPluginRef.current.resumeRecording();
      setRecPaused(false);

      let elapsed = recDuration;
      recTimerRef.current = setInterval(() => {
        elapsed += 1;
        setRecDuration(elapsed);
        if (elapsed >= recMaxDuration) {
          clearInterval(recTimerRef.current);
          recTimerRef.current = null;
          stopRecording();
        }
      }, 1000);
    }
  }, [recDuration, stopRecording]);

  const cancelRecording = useCallback(() => {
    if (recTimerRef.current) {
      clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }

    const rec = recPluginRef.current;
    if (rec) {
      try { if (rec.isRecording()) rec.stopRecording(); } catch { /* ignore */ }
      try {
        if (rec.stream) {
          rec.stream.getTracks().forEach(t => t.stop());
          rec.stream = null;
        }
      } catch { /* ignore */ }
      try { rec.stopMic(); } catch { /* ignore */ }
    }
    if (recWsRef.current) {
      try { recWsRef.current.destroy(); } catch { /* ignore */ }
    }
    recWsRef.current = null;
    recPluginRef.current = null;
    recCancelRef.current = true; // Mark as cancelling

    // Discard any recorded audio completely
    if (voiceBlobUrlRef.current) {
      URL.revokeObjectURL(voiceBlobUrlRef.current);
      voiceBlobUrlRef.current = null;
    }
    setVoiceBlob(null);
    setVoiceBlobUrl(null);

    setRecording(false);
    if (onStopRecording) onStopRecording();
    setRecPaused(false);
    recStoppingRef.current = false;
  }, []);

  const cleanupRecorder = () => {
    const rec = recPluginRef.current;
    const ws = recWsRef.current;
    if (rec) {
      try {
        if (rec.stream) {
          rec.stream.getTracks().forEach(t => t.stop());
          rec.stream = null;
        }
      } catch { /* ignore */ }
      try { rec.stopMic(); } catch { /* ignore */ }
    }
    if (ws) {
      try { ws.destroy(); } catch { /* ignore */ }
    }
    recWsRef.current = null;
    recPluginRef.current = null;
  };

  // Cleanup recorder on unmount
  useEffect(() => {
    return () => {
      if (recTimerRef.current) clearInterval(recTimerRef.current);
      cleanupRecorder();
    };
  }, []);

  // Start recording when waveform container appears
  useEffect(() => {
    if (recording && recWaveRef.current && !recWsRef.current) {
      // Clear any existing voice preview before starting new recording
      if (voiceBlobUrlRef.current) {
        URL.revokeObjectURL(voiceBlobUrlRef.current);
        voiceBlobUrlRef.current = null;
      }
      setVoiceBlob(null);
      setVoiceBlobUrl(null);
      startRecording();
    }
  }, [recording, startRecording]);

  const handleMicClick = () => {
    if (recording) return;

    // Clear any existing voice preview before starting new recording
    if (voiceBlobUrlRef.current) {
      URL.revokeObjectURL(voiceBlobUrlRef.current);
      voiceBlobUrlRef.current = null;
    }
    setVoiceBlob(null);
    setVoiceBlobUrl(null);

    // Show the recorder bar (will trigger startRecording via useEffect)
    setRecording(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasMedia = files.length > 0 || voiceBlob;
  const canSend = content.trim() || hasMedia;

  // Send voice message while recording (one-click send)
  const handleSendWhileRecording = () => {
    if (sending || !recording) return;

    // Set flag to send after recording stops
    recSendAfterStopRef.current = true;

    // Stop recording - will trigger record-end callback which will send
    stopRecording();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSend || sending) return;

    setSending(true);
    try {
      await onSend(content.trim(), files, voiceBlob);

      setContent('');
      setFiles([]);
      previews.forEach(p => URL.revokeObjectURL(p.url));
      setPreviews([]);
      if (voiceBlobUrl) URL.revokeObjectURL(voiceBlobUrl);
      voiceBlobUrlRef.current = null;
      setVoiceBlob(null);
      setVoiceBlobUrl(null);
      setIsTyping(false);
      onStopTyping();

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.overflowY = 'hidden';
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="message-input-wrapper">
      {replyTo && (
        <div className="reply-bar">
          <div className="reply-bar-content">
            <span className="reply-bar-label">{t('chat.replyingTo')} <strong>{replyTo.user?.display_name}</strong></span>
            <span className="reply-bar-text">
              {replyTo.is_deleted ? t('chat.messageDeleted') : (replyTo.content || '').substring(0, 80)}
            </span>
          </div>
          <button className="reply-bar-close" onClick={onCancelReply}>
            <IoCloseCircle />
          </button>
        </div>
      )}

      {/* Media previews */}
      {previews.length > 0 && (
        <div className="media-preview-strip">
          {previews.map((preview, index) => (
            <div key={index} className="media-preview-item">
              {preview.type === 'video' ? (
                <video src={preview.url} className="media-preview-thumb" />
              ) : (
                <img src={preview.url} alt={preview.name} className="media-preview-thumb" />
              )}
              <button className="media-preview-remove" onClick={() => removeFile(index)}>
                <IoClose />
              </button>
              {preview.type === 'video' && <span className="media-preview-badge">VIDEO</span>}
            </div>
          ))}
        </div>
      )}

      {/* Voice preview (after recording is done) */}
      {voiceBlobUrl && !recording && (
        <div className="voice-preview-bar">
          <VoicePlayer voiceUrl={voiceBlobUrl} duration={0} compact />
          <button className="voice-preview-remove" onClick={removeVoice}>
            <IoClose />
          </button>
        </div>
      )}

      <form className="message-input-form" onSubmit={handleSubmit}>
        {recording ? (
          /* Inline voice recorder — replaces textarea and action buttons */
          <div className="inline-recorder">
            <button type="button" className="rec-btn rec-delete" onClick={cancelRecording} title="Discard">
              <IoTrashOutline />
            </button>

            {recPaused ? (
              <button type="button" className="rec-btn rec-pause" onClick={resumeRecording} title="Resume">
                <IoPlayCircle />
              </button>
            ) : (
              <button type="button" className="rec-btn rec-pause" onClick={pauseRecording} title="Pause">
                <IoPauseCircle />
              </button>
            )}

            <div className="rec-wave-area">
              <div className="rec-waveform" ref={recWaveRef}></div>
            </div>

            <span className="rec-timer">{formatTime(recDuration)}</span>

            <button
              type="button"
              className="rec-btn rec-stop"
              onClick={stopRecording}
              disabled={recDuration < 1}
              title="Done"
            >
              <IoStopCircle />
            </button>
          </div>
        ) : (
          /* Normal input row */
          <>
            <div className="input-actions">
              <button
                type="button"
                className="input-action-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={files.length >= 4 || !!voiceBlob}
                title={t('chat.attachMedia', 'Attach image or video')}
              >
                <IoImageOutline />
              </button>
              <button
                type="button"
                className="input-action-btn"
                onClick={handleMicClick}
                disabled={files.length > 0 || !!voiceBlob}
                title={t('chat.recordVoice', 'Record voice message')}
              >
                <IoMicOutline />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <textarea
              ref={textareaRef}
              className="message-input"
              value={disabled ? '' : content}
              onChange={disabled ? undefined : handleChange}
              placeholder={disabled ? t('common.loading') : t('chat.messagePlaceholder')}
              rows="1"
              disabled={disabled}
              style={{ height: 'auto', overflowY: 'hidden' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </>
        )}

        <button
          type="submit"
          className="send-button"
          disabled={disabled || (recording ? false : (!canSend || sending))}
          onClick={recording ? handleSendWhileRecording : undefined}
        >
          <IoSend />
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
